/**
 * Module 1: what I2Q does for you (1 minute). Shadow mode, the three steps of a case
 * (read the mail, check the draft, do the one action), where everything else sits.
 *
 * Round 3 (12 Oct 2026): the "How it works" figure on w1 (so the quick start shows it)
 * and the three-step strip on w2; every sentence in Simplified Technical English.
 */

import type { Module } from "@/features/training/types";

import { checkSlide } from "../check-slide";

export const MODULE: Module = {
  id: "welcome",
  title: "What I2Q does for you",
  menuTitle: "Welcome",
  minutes: 1,
  kind: "lesson",
  goals: [
    "Know that I2Q only prepares. It never sends.",
    "Know that you reply to the customer from Outlook and quote in F&O.",
    "Know the three steps of every case.",
  ],
  helpTopics: ["what-it-is"],
  slides: [
    {
      id: "w1",
      tone: "dark",
      title: "I2Q reads inquiries and prepares drafts. You decide and send.",
      job: "Look at the badge at the top of every page.",
      body: [
        {
          kind: "text",
          text: "I2Q reads the inquiry mails in your shared mailbox. It finds the customer, the items and the prices. Then it prepares the draft and the reply.",
        },
        { kind: "figure", figure: "how-it-works" },
        {
          kind: "text",
          text: "The badge says `Shadow mode: read-only, nothing is sent`. The help page says it too: **Nothing is sent and nothing is written to F&O.**",
        },
        {
          kind: "text",
          text: "Every case has three steps, in this order: **Read the mail**, **Check the draft**, **Do the one action**.",
        },
        {
          kind: "list",
          items: [
            "I2Q prepares the draft and the reply.",
            "You read the mail and check the draft.",
            "You press the one action and send the reply from Outlook.",
            "You make the quotation in F&O.",
          ],
        },
      ],
      interaction: {
        id: "w1-badge",
        kind: "hotspots",
        required: true,
        minOpen: 1,
        stage: { view: "queue", preset: "default" },
        spots: [
          {
            anchor: "shadow-badge",
            label: "Shadow mode",
            text: "I2Q reads and prepares. It sends nothing to the customer and writes nothing to F&O. On a phone you see only the icon.",
          },
        ],
      },
      check: "Every mail to the customer goes out from your Outlook. I2Q never sends a mail.",
      labels: [
        "Shadow mode: read-only, nothing is sent",
        "Nothing is sent and nothing is written to F&O.",
      ],
    },
    {
      id: "w2",
      tone: "light",
      title: "The three steps, and where the rest sits",
      job: "Open at least 3 cards to see what each step means.",
      body: [
        { kind: "figure", figure: "three-steps" },
        {
          kind: "text",
          text: "Each case has three steps. The queue is where you find your work. Open the cards below. Each card names the module that teaches it.",
        },
      ],
      interaction: {
        id: "w2-jobs",
        kind: "reveal",
        required: true,
        minOpen: 3,
        cards: [
          {
            id: "w2-find",
            front: "Find your work",
            back: "The queue shows today, one row per case, with one action word. The backlog chips open your whole backlog.",
            module: "queue",
          },
          {
            id: "w2-read",
            front: "Read the mail",
            back: "The mail is a compact card at the top of the case. The customer line sits in it, under the sender. Confirmed means I2Q is sure. Confirm is one click. Find customer opens the search.",
            module: "customer",
          },
          {
            id: "w2-draft",
            front: "Check the draft",
            back: "The draft is a table in the middle of the page. Each row is one line, with an icon and a word: matched, confirm, pick or no item. The lines that need you are on top. Compare the quantity, the unit and the price with the customer's own words.",
            module: "lines",
          },
          {
            id: "w2-action",
            front: "Do the one action",
            back: "The big button names the next step. It changes as you work, from Confirm to Copy the reply. Nothing is sent from there.",
            module: "reply",
          },
          {
            id: "w2-more",
            front: "Everything else",
            back: "Take, Set status and Give feedback sit under More. Details opens the side sheet with the checks, the quotation and the history.",
            module: "status",
          },
          {
            id: "w2-feedback",
            front: "Tell us what is wrong",
            back: "Give feedback tells the build team. To change the case, use Confirm, Pick item or No item on the row.",
            module: "extras",
          },
        ],
      },
      check: "You check each step. I2Q prepares, you decide.",
    },
    {
      id: "w3",
      tone: "grey",
      title: "What you can do after 20 minutes",
      job: "Read what you will be able to do.",
      body: [
        {
          kind: "list",
          items: [
            "Find every open case, also the old ones.",
            "Confirm the right customer, with evidence.",
            "Read the four status words of the draft.",
            "Find a wrong quantity or a guessed item.",
            "Press the one action and copy the reply.",
            "Correct the reply in Outlook and send it.",
            "Close and reopen cases under More. Colleagues then see who works on what.",
          ],
        },
        {
          kind: "note",
          tone: "info",
          title: "If you have 5 minutes, do the quick start.",
          text: "The quick start takes 5 minutes. It covers the five most common mistakes.",
        },
      ],
      interaction: {
        id: "w3-check",
        kind: "check",
        required: true,
        question: {
          id: "w3-q",
          topic: "shadow",
          stem: "You click `Copy the reply`. Who sends the mail?",
          options: [
            {
              id: "w3-q-a",
              text: "I2Q, from the shared mailbox.",
              correct: false,
              fb: "No. I2Q never sends. It only puts the text on your clipboard.",
            },
            {
              id: "w3-q-b",
              text: "You, from Outlook.",
              correct: true,
              fb: "Right. Paste the text into your reply in Outlook. Check it. Send it.",
            },
            {
              id: "w3-q-c",
              text: "F&O, with the quotation.",
              correct: false,
              fb: "No. I2Q writes nothing to F&O. You make the quotation in F&O yourself.",
            },
            {
              id: "w3-q-d",
              text: "Nobody. The mail goes out when you close the case.",
              correct: false,
              fb: "No. When you close a case, only its status in I2Q changes. Nothing goes to the customer.",
            },
          ],
        },
      },
      check: "If the customer got no mail from your Outlook, the customer got no reply.",
      labels: ["Copy the reply"],
    },
    checkSlide({
      id: "w4",
      tone: "light",
      title: "Check: F&O",
      topic: "shadow",
      stem: "What does I2Q write to F&O?",
      answers: [
        [
          "The quotation, after you copy the reply.",
          false,
          "No. I2Q writes nothing to F&O. You make the quotation there yourself.",
        ],
        [
          "Nothing. You make the quotation in F&O yourself.",
          true,
          "Right. In shadow mode I2Q writes nothing to F&O.",
        ],
        ["The confirmed customer.", false, "No. I2Q saves the confirmed customer in I2Q only."],
        [
          "The prices of the matched lines.",
          false,
          "No. I2Q reads prices from F&O. It never writes them.",
        ],
      ],
      check: "I2Q reads F&O. It never writes to it.",
    }),
    checkSlide({
      id: "w5",
      tone: "grey",
      title: "Check: what you do",
      topic: "shadow",
      stem: "I2Q prepared a reply. What do you do?",
      answers: [
        [
          "Nothing. The reply goes out on its own.",
          false,
          "No. Nothing goes out on its own. You send every reply.",
        ],
        [
          "Wait until I2Q sends it in the evening.",
          false,
          "No. I2Q never sends a reply, in the evening or at any other time.",
        ],
        [
          "Forward the case to your manager.",
          false,
          "No. Your manager approves held prices only. The reply is yours.",
        ],
        [
          "Check it, correct it and send it from Outlook.",
          true,
          "Right. I2Q prepares. You check and send.",
        ],
      ],
      check: "You decide what the customer gets.",
    }),
  ],
};
